<script setup lang="ts">
/* Import ORDER is load-bearing here and must NOT be sorted: the TinyMCE core
   (`import tinymce from 'tinymce'`) defines the global `tinymce` that the theme/model/icons/plugins
   — and the runtime-loaded `langs/de.js` (`tinymce.addI18n(...)`) — reference at import/exec time.
   Core MUST come first; re-sorting throws "ReferenceError: tinymce is not defined". */
import tinymce from 'tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver/theme';
import 'tinymce/icons/default/icons';
import 'tinymce/plugins/link/plugin';
import 'tinymce/plugins/lists/plugin';
import 'tinymce/skins/ui/oxide/skin.css';
// Default editor content styles, injected into the editor iframe via content_style — same stylesheet
// Angular loads through the bundled skin, so in-editor rendering (font, margins, line-height) matches.
import contentCss from 'tinymce/skins/content/default/content.css?inline';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

export interface WysiwygMenuOption {
    title: string
    value?: string
    menu?: WysiwygMenuOption[]
}

const props = withDefaults(
    defineProps<{
        modelValue?: string | null
        toolbar?: string
        singleLineMode?: boolean
        height?: number
        maxTextLength?: number
        defaultContent?: string | null
        blocks?: string
        relList?: WysiwygMenuOption[] | false
        linkClassList?: WysiwygMenuOption[]
        targetList?: WysiwygMenuOption[] | false
    }>(),
    {
        modelValue: null,
        toolbar: 'bold bullist link | undo redo',
        singleLineMode: false,
        height: 200,
        maxTextLength: undefined,
        defaultContent: null,
        blocks: undefined,
        relList: false,
        linkClassList: undefined,
        targetList: false,
    },
);

const emit = defineEmits<{
    'update:modelValue': [value: string]
    blur: []
}>();

const elementId = `kgr-wysiwyg-control-${Math.round(Math.random() * 999999)}`;
const showPlaceholder = ref(true);
const remaining = ref<number | null>(null);
const isInvalid = ref(false);
const initialized = ref(false);

let editor: any = null;

function getTextContent(html: string | null | undefined): string {
    if (!html) {
        return '';
    }
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent ?? '';
}

function getHtmlWithoutTrailingEmptyLines(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    const children = div.children;
    for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];
        if (['UL'].includes(child.tagName) && child.textContent!.trim().length > 0) {
            child.innerHTML = getHtmlWithoutTrailingEmptyLines(child.innerHTML);
        }
        if (
            ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'UL'].includes(child.tagName)
            && child.textContent!.trim().length === 0
        ) {
            div.removeChild(child);
        } else {
            break;
        }
    }
    return div.innerHTML;
}

function updateCharCount(content: string | null) {
    if (!props.maxTextLength) {
        return;
    }
    const text = getTextContent(content).trim();
    remaining.value = props.maxTextLength - text.length;
    isInvalid.value = remaining.value < 0;
}

function initTinyMce() {
    if (initialized.value) {
        return;
    }

    tinymce.init({
        license_key: 'gpl',
        selector: `#${elementId}`,
        skin: false,
        content_css: false,
        content_style: contentCss,
        branding: false,
        plugins: ['link', 'lists'],
        language: 'de',
        language_url: `${import.meta.env.BASE_URL}langs/de.js`,
        toolbar: props.toolbar,
        block_formats: props.blocks,
        entity_encoding: 'raw',
        link_assume_external_targets: 'https',
        textpattern_patterns: [
            { start: '*', end: '*', format: 'italic' },
            { start: '**', end: '**', format: 'bold' },
            { start: '1. ', cmd: 'InsertOrderedList' },
            { start: '* ', cmd: 'InsertUnorderedList' },
            { start: '- ', cmd: 'InsertUnorderedList' },
        ],
        target_list: props.targetList,
        rel_list: props.relList,
        link_class_list: props.linkClassList,
        link_title: false,
        link_default_protocol: 'https',
        menubar: false,
        statusbar: !props.singleLineMode,
        elementpath: false,
        height: props.singleLineMode ? 89 : props.height,
        max_height: props.singleLineMode ? 89 : 2000,
        resize: !props.singleLineMode,
        paste_as_text: true,
        paste_postprocess: (_plugin: any, args: any) => {
            if (props.singleLineMode) {
                // innerText strips HTML (unlike textContent), which is exactly what singleLineMode needs
                // eslint-disable-next-line unicorn/prefer-dom-node-text-content
                args.node.innerHTML = args.node.innerText;
            }
        },
        setup: (ed: any) => {
            editor = ed;

            ed.on('init', () => {
                initialized.value = true;
                if (props.modelValue) {
                    ed.setContent(props.modelValue);
                    showPlaceholder.value = false;
                }
                updateCharCount(props.modelValue ?? null);
            });

            ed.on('focusin', () => {
                if (!ed.getContent() && props.defaultContent) {
                    ed.setContent(props.defaultContent);
                }
                showPlaceholder.value = false;
            });

            ed.on('blur', () => {
                const content = ed.getContent();
                if (content) {
                    const cleaned = getHtmlWithoutTrailingEmptyLines(content);
                    if (cleaned !== content) {
                        ed.setContent(cleaned);
                        emit('update:modelValue', cleaned);
                    }
                }
                if (!ed.getContent()) {
                    showPlaceholder.value = true;
                }
                emit('blur');
            });

            ed.on('keyup change ExecCommand SetContent', () => {
                if (initialized.value) {
                    const content = ed.getContent();
                    emit('update:modelValue', content);
                    updateCharCount(content);
                }
            });

            if (props.singleLineMode) {
                ed.on('keydown', (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                });
            }
        },
    });
}

watch(
    () => props.modelValue,
    (newVal, oldVal) => {
        if (!editor || !initialized.value) {
            return;
        }
        const current = editor.getContent();
        if (current !== newVal && newVal !== oldVal) {
            editor.setContent(newVal ?? '');
        }
    },
);

watch(
    () => props.defaultContent,
    (newVal, oldVal) => {
        if (!editor || !initialized.value) {
            return;
        }
        const previousText = getTextContent(oldVal ?? '').trim();
        const editorText = getTextContent(editor.getContent()).trim();
        if (previousText === editorText) {
            editor.setContent(newVal ?? '');
        }
    },
);

onMounted(() => {
    initTinyMce();
});

onBeforeUnmount(() => {
    if (typeof tinymce !== 'undefined' && editor) {
        tinymce.remove(editor);
    }
    editor = null;
    initialized.value = false;
});
</script>

<template>
    <div
        class="wysiwyg-wrapper"
        :style="{ minHeight: `${height}px` }"
        :class="{ 'is-singleLine': singleLineMode }"
    >
        <span
            v-if="maxTextLength"
            class="remainingCharsCounter"
            :class="{ 'is-invalid': isInvalid }"
        >{{ remaining }}</span>
        <textarea
            :id="elementId"
            class="kgr-hidden"
        ></textarea>
        <div
            v-if="showPlaceholder"
            class="placeholder"
        >
            <slot></slot>
        </div>
    </div>
</template>
